import type { ReactNode } from 'react';
import { Skeleton } from './primitives';

/**
 * Metric composition.
 *
 * The instruction this file exists to obey: not every number deserves the same size.
 * Four equally loud cards force the reader to do the ranking themselves, which is the
 * defining failure of admin-template dashboards. Here the hierarchy is decided in the
 * design — one primary figure, a supporting pair, then exceptions in a quiet row.
 */

/** The composed day panel: one surface, asymmetric columns. */
export function DayPanel({ children }: { children: ReactNode }) {
  return <div className="day-panel">{children}</div>;
}

/** The single number that answers "what happened today". */
export function PrimaryMetric({
  label,
  value,
  caption,
  loading,
  children,
}: {
  label: string;
  value: number | string;
  caption?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="day-primary">
      <p className="eyebrow">{label}</p>
      {loading ? (
        <Skeleton height={64} width="42%" />
      ) : (
        <p className="metric-primary-value num">{value}</p>
      )}
      {caption && !loading && <div className="metric-primary-caption">{caption}</div>}
      {children}
    </div>
  );
}

/** A supporting figure — half the weight of the primary, twice that of an exception. */
export function SecondaryMetric({
  label,
  value,
  share,
  tone = 'neutral',
  loading,
}: {
  label: string;
  value: number | string;
  /** 0–1; renders a hairline proportion bar, not a chart. */
  share?: number;
  tone?: 'neutral' | 'accent';
  loading?: boolean;
}) {
  return (
    <div className="day-secondary">
      <p className="metric-secondary-label">{label}</p>
      {loading ? (
        <Skeleton height={28} width="52%" />
      ) : (
        <p className="metric-secondary-value num">{value}</p>
      )}
      {share !== undefined && !loading && (
        <span className="metric-bar" aria-hidden="true">
          <span
            className={`metric-bar-fill${tone === 'accent' ? ' is-accent' : ''}`}
            style={{ transform: `scaleX(${Math.max(0, Math.min(1, share))})` }}
          />
        </span>
      )}
    </div>
  );
}

/**
 * An operational exception. Stays neutral when the count is zero — a dashboard should
 * not colour itself in when nothing is wrong.
 */
export function ExceptionMetric({
  label,
  value,
  tone = 'neutral',
  icon,
  loading,
  onClick,
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'warning' | 'accent';
  icon?: ReactNode;
  loading?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <span className="exception-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="exception-body">
        <span className="exception-label">{label}</span>
        {loading ? (
          <Skeleton height={18} width={40} />
        ) : (
          <span className="exception-value num">{value}</span>
        )}
      </span>
    </>
  );

  const className = `exception${tone !== 'neutral' ? ` exception-${tone}` : ''}${
    onClick ? ' is-clickable' : ''
  }`;

  if (onClick) {
    return (
      <button className={className} onClick={onClick} type="button">
        {content}
      </button>
    );
  }
  return <div className={className}>{content}</div>;
}

export function ExceptionRow({ children }: { children: ReactNode }) {
  return <div className="exception-row">{children}</div>;
}
